/**
 * Utility for maintaining alphabetically sorted file/folder lists in cache
 * Ensures folders appear first, then files, both sorted alphabetically (case-insensitive)
 */

/**
 * Insert a file or folder into a sorted array
 * @param files - Existing array of files/folders
 * @param item - Item to insert (file or folder)
 * @returns New array with item inserted in correct sorted position
 */
export const insertSorted = (files: any[], item: any): any[] => {
  const newFiles = [...files]
  const isItemFolder = item.isDir

  // Find insertion index - folders first, then alphabetical
  let insertIndex = newFiles.findIndex((f: any) => {
    // If item is folder and current is file, item goes before
    if (isItemFolder && !f.isDir) return true
    // If item is file and current is folder, keep searching
    if (!isItemFolder && f.isDir) return false

    // Both same type (folder/file), compare alphabetically (case-insensitive)
    return item.name.toLowerCase() < f.name.toLowerCase()
  })

  // If not found, append to end
  if (insertIndex === -1) insertIndex = newFiles.length

  newFiles.splice(insertIndex, 0, item)

  return newFiles
}

/**
 * Update query cache data by inserting an item in sorted position
 * @param oldData - Existing query data (can be infinite query or regular query)
 * @param item - Item to insert
 * @returns Updated query data with item inserted in sorted position
 */
export const addItemToSortedCache = (oldData: any, item: any): any => {
  if (!oldData) return oldData

  // For infinite queries (pagination)
  if (oldData.pages) {
    return {
      ...oldData,
      pages: oldData.pages.map((page: any, pageIndex: number) => {
        // Add item to the first page only
        if (pageIndex === 0) {
          return {
            ...page,
            data: insertSorted(page.data || [], item),
            totalCount: (page.totalCount || 0) + 1,
          }
        }

        return page
      }),
    }
  }

  // For regular queries
  if (oldData.data) {
    return {
      ...oldData,
      data: insertSorted(oldData.data || [], item),
      totalCount: (oldData.totalCount || 0) + 1,
    }
  }

  return oldData
}
